import Drawer from "@/components/drawer";
import { CrewProvider } from "@/context/CrewProvider";
import { useSupabase } from "@/hooks/useSupabase";
import { useBadgeCount } from "@/hooks/useBadgeCount";
import { trpc } from "@/trpc/react";
import { Redirect, useRouter } from "expo-router";
import React, { useEffect } from "react";
import { ActivityIndicator, View } from "react-native";
import colors from "@/constants/Colors";
import HoldingPage from "./holding";
import PushPermissionBanner from "@/components/notification/push-permission-banner";
import UnreadMessagesBanner from "@/components/messages/unread-messages-banner";
import BiometricSetupBanner from "@/components/auth/biometric-setup-banner";

export default function AppLayout() {
  const { user, signOut } = useSupabase();
  const router = useRouter();

  // Redirect to auth if no user
  if (!user) {
    return <Redirect href="/(auth)" />;
  }

  const handleAuthError = () => {
    if (signOut) {
      signOut().then(() => router.replace('/(auth)'));
    } else {
      // Fallback if signOut is undefined
      router.replace('/(auth)');
    }
  };

  // OneSignal external_id is now owned by AuthProvider's user-id effect,
  // which fires for every auth path (password, biometric, deep-link reset,
  // cold-start session restore). Keeping a duplicate here led to redundant
  // login() calls and a hooks-after-conditional-return rule violation.

  const {
    isLoading,
    data: currentCrewMember,
    error,
  } = trpc.crew.getByUserId.useQuery(
    { user_id: user.id },
    {
      // Don't retry on auth errors to avoid loops
      retry: (failureCount, error) => {
        if (
          error.message?.includes('You must be logged in') ||
          error.message?.includes('UNAUTHORIZED')
        ) {
          // Handle auth error by redirecting to login
          setTimeout(() => {
            handleAuthError();
          }, 100);
          return false;
        }
        return failureCount < 3;
      },
      // Avoid stale data causing issues
      staleTime: 30000, // 30 seconds
    }
  );

  // Initialize badge count management (must be called before any conditional returns)
  // Hook will only run when crew member is available and onboarded
  useBadgeCount(currentCrewMember?.is_onboarded ? currentCrewMember?.id : undefined);

  // Handle authentication errors
  useEffect(() => {
    if (error &&
      (error.message?.includes('You must be logged in') ||
        error.message?.includes('UNAUTHORIZED'))) {
      handleAuthError();
    }
  }, [error, router]);

  if (isLoading) {
    return (
      <View className="flex-1 justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator color={colors.primary} />
      </View>
    )
  }

  if (!currentCrewMember || !currentCrewMember.user_id) {
    return <Redirect href="/(onboarding)" />;
  }

  // If not onboarded, show holding page
  if (!currentCrewMember.is_onboarded) {
    return <HoldingPage />;
  }

  // If onboarded, show drawer navigation
  return (
    <CrewProvider>
      {/*
        Top banners stack in priority order, but each banner internally
        defers to higher-priority ones so only ONE renders at a time:
          push (critical) > unread messages (actionable) > biometric (optional)
      */}
      <PushPermissionBanner />
      <UnreadMessagesBanner />
      <BiometricSetupBanner />
      <Drawer />
    </CrewProvider>
  )
}
